import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { addUpsell, getUpsellEligibility, type UpsellEligibility } from '../../lib/upsell';
import { formatCurrency } from '../../lib/format';

interface Props {
  orderId: string;
  onClose: () => void;
  onAdded: () => void;
}

export function UpsellVlekkenbaasDialog({ orderId, onClose, onAdded }: Props) {
  const [eligibility, setEligibility] = useState<UpsellEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUpsellEligibility(orderId, 'vlekkenbaas', 0)
      .then((data) => {
        if (cancelled) return;
        setEligibility(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const unitPrice = eligibility?.pricePerM2InclVat ?? 0;
  // Geen kortingsoptie meer. De checkbox toonde 30% korting die het schrijfpad
  // nooit doorvoerde (`KledaUpsellService.confirmVlekkenbaas` negeerde `korting`
  // volledig), dus de klant betaalde altijd de volle prijs terwijl de
  // medewerker dacht korting te geven. Besloten 07-08-2026: checkbox eruit in
  // plaats van de korting aanzetten — vlekkenbaas is een vast upsellproduct.
  const total = unitPrice * quantity;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await addUpsell({
        workOrderId: orderId,
        type: 'vlekkenbaas',
        quantity,
        discount: 0,
      });
      if (!result.success) {
        setError(result.message || 'Upsell toevoegen mislukt');
        setSubmitting(false);
        return;
      }
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upsell toevoegen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Vlekkenbaas toevoegen</h2>
          {eligibility?.workOrderNumber && (
            <p className="text-xs text-slate-500 mt-1">
              {eligibility.workOrderNumber}
              {eligibility.customerName ? ` — ${eligibility.customerName}` : ''}
            </p>
          )}
        </div>

        <div className="px-6 py-4 space-y-4">
          {loading && <p className="text-sm text-slate-500">Laden…</p>}
          {!loading && eligibility?.alreadyOrdered && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Vlekkenbaas is al besteld voor deze order.
            </p>
          )}
          {!loading && !eligibility?.alreadyOrdered && (
            <>
              <div>
                <label className="text-sm text-slate-700 block mb-1">Aantal sets</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    className="w-8 h-8 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-30"
                  >
                    −
                  </button>
                  <span className="text-base font-medium text-slate-900 w-8 text-center">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(5, q + 1))}
                    disabled={quantity >= 5}
                    className="w-8 h-8 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-30"
                  >
                    +
                  </button>
                  <span className="text-xs text-slate-500 ml-2">{formatCurrency(unitPrice)} per set</span>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex justify-between text-sm">
                <span className="text-slate-500">Totaal</span>
                <span className="font-medium text-slate-900">{formatCurrency(total)}</span>
              </div>
            </>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loading || !!eligibility?.alreadyOrdered}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || loading || !!eligibility?.alreadyOrdered
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Toevoegen…' : 'Bevestigen'}
          </button>
        </div>
      </div>
    </div>
  );
}
